/**
 * Recursively serializes an Error object to capture non-enumerable properties
 * like message, stack, and cause. This is useful for logging, storing in databases,
 * or sending over the wire where Error objects don't serialize properly by default.
 *
 * @param error - The error to serialize (can be any type)
 * @returns A plain object representation of the error with all important properties
 *
 * @example
 * ```ts
 * try {
 *   // some code that throws
 * } catch (error) {
 *   const serialized = serializeError(error)
 *   await logger.error('Something failed', { error: serialized })
 * }
 * ```
 */
export const serializeError = (error: unknown): unknown => {
  if (!(error instanceof Error)) {
    return error
  }

  return {
    name: error.name,
    message: error.message,
    stack: error.stack,
    cause: error.cause ? serializeError(error.cause) : undefined,
    // For Zod errors specifically
    ...(error.name === 'ZodError' && 'issues' in error
      ? { issues: error.issues }
      : {}),
  }
}
